// services/ugc/prayerApi.ts

import baseApi from "../../lib/baseApi";
import uploadApi from "../../lib/uploadApi";
import type { AxiosRequestConfig, AxiosProgressEvent } from "axios";

import type {
  PrayerDTO,
  PrayerFeedResponse,
  CreatePrayerPayload,
  CreatePrayerResponsePayload,
} from "../../types/ugc/prayerTypes";

import { UGC_ENDPOINTS } from "./endpoint/endpoints";

const ROOT = UGC_ENDPOINTS.prayers.root;


// ------------------------------------------------------------
// Upload progress helpers (shared pattern)
// ------------------------------------------------------------
type ProgressOpts = {
  onUploadProgress?: (pct: number) => void;
  signal?: AbortSignal;
};

const withProgress = (opts?: ProgressOpts): AxiosRequestConfig => ({
  signal: opts?.signal,
  onUploadProgress: (evt: AxiosProgressEvent) => {
    if (!opts?.onUploadProgress) return;

    const pct =
      typeof evt.progress === "number"
        ? Math.round(evt.progress * 100)
        : evt.total
          ? Math.round((evt.loaded / evt.total) * 100)
          : 0;

    opts.onUploadProgress(pct);
  },
});

export const prayerApi = {
  // ----------------------------------------------------------
  // Feed
  // ----------------------------------------------------------
  getFeed: async (cursor?: string) => {
    const { data } = await baseApi.get<PrayerFeedResponse>(
      `${ROOT}/feed/`,
      cursor ? { params: { cursor } } : undefined
    );
    return data;
  },

  // ----------------------------------------------------------
  // Explore
  // ----------------------------------------------------------
  getExplore: async (page?: number) => {
    const { data } = await baseApi.get<PrayerFeedResponse>(
      `${ROOT}/explore/`,
      page ? { params: { page } } : undefined
    );
    return data;
  },

  // ----------------------------------------------------------
  // My prayers
  // ----------------------------------------------------------
  getMine: async (page?: number) => {
    const { data } = await baseApi.get<PrayerFeedResponse>(
      `${ROOT}/me/`,
      page ? { params: { page } } : undefined
    );
    return data;
  },

  // ----------------------------------------------------------
  // Detail
  // ----------------------------------------------------------
  getBySlug: async (slug: string) => {
    const { data } = await baseApi.get<PrayerDTO>(`${ROOT}/${slug}/`);
    return data;
  },

  // ----------------------------------------------------------
  // Create
  // ----------------------------------------------------------
  create: async (
    payload: FormData | CreatePrayerPayload,
    opts?: ProgressOpts
  ) => {
    const isFormData =
      typeof FormData !== "undefined" && payload instanceof FormData;

    const client = isFormData ? uploadApi : baseApi;

    const { data } = await client.post<PrayerDTO>(
      `${ROOT}/`,
      payload,
      withProgress(opts)
    );

    return data;
  },

  // ----------------------------------------------------------
  // Update
  // ----------------------------------------------------------
  update: async (
    slug: string,
    payload: FormData | Partial<CreatePrayerPayload>
  ) => {
    const client =
      typeof FormData !== "undefined" && payload instanceof FormData
        ? uploadApi
        : baseApi;

    const { data } = await client.patch<PrayerDTO>(
      `${ROOT}/${slug}/`,
      payload
    );

    return data;
  },

  // ----------------------------------------------------------
  // Delete
  // ----------------------------------------------------------
  remove: async (slug: string) => {
    const { status } = await baseApi.delete(`${ROOT}/${slug}/`);
    return status === 204;
  },

  // ----------------------------------------------------------
  // Create or Update Response
  // POST / PATCH /respond/
  // ----------------------------------------------------------
// Create or Update Response
// POST /respond/ then refetch full PrayerDTO
respond: async (
  slug: string,
  payload: FormData | CreatePrayerResponsePayload,
  opts?: ProgressOpts
) => {
  const client =
    typeof FormData !== "undefined" && payload instanceof FormData
      ? uploadApi
      : baseApi;

  // 1) Save response (backend returns PrayerResponseDTO, not PrayerDTO)
  await client.post(`${ROOT}/${slug}/respond/`, payload, withProgress(opts));

  // 2) Refetch full prayer (contains response + job_target gating payload)
  return prayerApi.getBySlug(slug);
},

  // ----------------------------------------------------------
  // Delete Response
  // ----------------------------------------------------------
  deleteResponse: async (slug: string) => {
    const { status } = await baseApi.delete(
      `${ROOT}/${slug}/delete_response/`
    );
    return status === 204;
  },
};

export default prayerApi;